import { RxLifespanDto } from "../../../../api-client";
import { useEffect, useState, useMemo } from "react";
import useStore from "../../../../helpers/useStore";
import { BIContext } from "../../../../stores/BIDashboard/biDashboard.provider";
import { useDebounce } from "../../../Transactions/Insurance/Base/hooks/useDebounce";

const useRxBlankoLifespan = (startDate: Date, endDate: Date, locationIds?: string[]) => {
  const BIStore = useStore(BIContext);
  const { getRxBlankoLifespanStats } = BIStore;

  // Debounce date changes
  const debounceStartDate = useDebounce(startDate, 300);
  const debounceEndDate = useDebounce(endDate, 300);

  // Memoize locationIds to avoid unnecessary re-renders
  const memoizedLocationIds = useMemo(() => {
    if (!locationIds || locationIds.length === 0) {
      return undefined;
    }
    return locationIds;
  }, [locationIds?.join(','), locationIds?.length]);

  // Store all fetched stats (for "all locations")
  const [allRxBlankoLifespan, setAllRxBlankoLifespan] = useState<RxLifespanDto[]>([]);
  // Store filtered stats per selected locations
  const [rxBlankoLifespan, setRxBlankoLifespan] = useState<RxLifespanDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);

        let response: RxLifespanDto[] | undefined;

        if (debounceStartDate && debounceEndDate) {
          // console.log(`[Rx Blanko Lifespan] Fetching data for date range: ${debounceStartDate} - ${debounceEndDate}`);
          // Always fetch all locations data, don't pass location filter to backend
          response = await getRxBlankoLifespanStats(debounceStartDate, debounceEndDate);
          // console.log("[Rx Blanko Lifespan] Backend responded with:", response);
        } else {
          // console.log("[Rx Blanko Lifespan] Fetching all data");
        }

        if (response && response.length > 0) {
          setAllRxBlankoLifespan(response);
          
          // Apply location filter immediately when setting data
          if (memoizedLocationIds && memoizedLocationIds.length > 0) {
            setRxBlankoLifespan(response.filter(p => memoizedLocationIds.includes(p.locationId)));
          } else {
            setRxBlankoLifespan(response);
          }
        } else {
          // console.log("[Rx Blanko Lifespan] No data found for the specified range.");
          setAllRxBlankoLifespan([]);
          setRxBlankoLifespan([]);
        }
      } catch (error) {
        // console.log("[Rx Blanko Lifespan] Error fetching Blanko Rx lifespan:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [debounceStartDate, debounceEndDate, getRxBlankoLifespanStats]);

  // Filter locally whenever locations change (but not when data is initially loaded)
  useEffect(() => {
    if (!isLoading && allRxBlankoLifespan.length > 0) {
      if (memoizedLocationIds && memoizedLocationIds.length > 0) {
        setRxBlankoLifespan(allRxBlankoLifespan.filter(p => memoizedLocationIds.includes(p.locationId)));
      } else {
        // No location selected → show all
        setRxBlankoLifespan(allRxBlankoLifespan);
      }
    }
  }, [memoizedLocationIds, allRxBlankoLifespan, isLoading]);

  const availableLocations = useMemo(() => {
    const locMap = new Map<string, string>();
    allRxBlankoLifespan.forEach((p) => {
      if (p.locationId) locMap.set(p.locationId, p.locationId);
    });
    return Array.from(locMap.entries()).map(([id, name]) => ({ id, name }));
  }, [allRxBlankoLifespan]);

  return { rxBlankoLifespan, isLoading, availableLocations };
};

export default useRxBlankoLifespan;